import React, { useEffect, useState } from "react";
import {
  XMarkIcon,
  CalendarDaysIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";

const ServiceBookingModal = ({ service, isOpen, onClose, onSuccess }) => {
  const { user, apiCall } = useAuth();

  const [pets, setPets] = useState([]);
  const [petId, setPetId] = useState("");
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [notes, setNotes] = useState("");

  const [loadingPets, setLoadingPets] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (isOpen && user) {
      loadPets();
      setError("");
      setSuccessMessage("");
    }
  }, [isOpen, user]);

  const loadPets = async () => {
    try {
      setLoadingPets(true);

      const response = await apiCall("/pets");
      const loadedPets = response?.data || response?.pets || [];

      setPets(Array.isArray(loadedPets) ? loadedPets : []);

      if (Array.isArray(loadedPets) && loadedPets.length > 0) {
        setPetId((prev) => prev || loadedPets[0].id);
      }
    } catch (error) {
      console.error("Load pets error:", error);
      setPets([]);
    } finally {
      setLoadingPets(false);
    }
  };

  const getTodayDateValue = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const buildDateTime = () => {
    if (!bookingDate || !bookingTime) {
      return "";
    }

    const [year, month, day] = bookingDate.split("-").map(Number);
    const [hour, minute] = bookingTime.split(":").map(Number);

    const selectedLocalDate = new Date(year, month - 1, day, hour, minute, 0);

    if (Number.isNaN(selectedLocalDate.getTime())) {
      return "";
    }

    return selectedLocalDate.toISOString();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");
    setSuccessMessage("");

    if (!user) {
      setError("Please sign in to book a service.");
      return;
    }

    if (!service?.id) {
      setError("Service is missing.");
      return;
    }

    if (!service?.company_id) {
      setError("Service company_id is missing.");
      return;
    }

    if (!petId) {
      setError("Please select a pet.");
      return;
    }

    const dateTime = buildDateTime();

    if (!dateTime) {
      setError("Please select date and time.");
      return;
    }

    try {
      setSubmitting(true);

      const response = await apiCall("/bookings", {
        method: "POST",
        body: JSON.stringify({
          company_id: service.company_id,
          service_id: service.id,
          pet_id: petId,
          date_time: dateTime,
          notes: notes || `Booking request for ${service.name}`,
        }),
      });

      if (!response?.success) {
        throw new Error(response?.error || "Failed to create booking.");
      }

      setSuccessMessage("Booking request sent to company for approval.");

      if (onSuccess) {
        onSuccess(response.data);
      }

      setTimeout(() => {
        onClose();
      }, 900);
    } catch (error) {
      console.error("Create booking error:", error);
      setError(error.message || "Failed to send booking request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !service) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-zinc-950/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-lg bg-white rounded-[32px] shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-zinc-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-orange-500 mb-2">
              <CalendarDaysIcon className="h-5 w-5" />

              <span className="text-[10px] font-black uppercase tracking-widest">
                Booking Request
              </span>
            </div>

            <h2 className="text-2xl font-black text-zinc-900">
              {service.name}
            </h2>

            <p className="text-sm font-medium text-zinc-500 mt-1">
              The request will be sent to the company for approval.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 transition-all"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2">
              Pet
            </label>

            <select
              value={petId}
              onChange={(event) => setPetId(event.target.value)}
              className="w-full bg-zinc-50 border border-zinc-100 rounded-2xl px-4 py-3 text-sm font-bold text-zinc-900 focus:ring-2 focus:ring-orange-500 outline-none"
              disabled={loadingPets}
            >
              {pets.length === 0 ? (
                <option value="">No pets found</option>
              ) : (
                pets.map((pet) => (
                  <option key={pet.id} value={pet.id}>
                    {pet.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2">
                Date
              </label>

              <input
                type="date"
                value={bookingDate}
                min={getTodayDateValue()}
                onChange={(event) => setBookingDate(event.target.value)}
                className="w-full bg-zinc-50 border border-zinc-100 rounded-2xl px-4 py-3 text-sm font-bold text-zinc-900 focus:ring-2 focus:ring-orange-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2">
                Time
              </label>

              <input
                type="time"
                value={bookingTime}
                onChange={(event) => setBookingTime(event.target.value)}
                className="w-full bg-zinc-50 border border-zinc-100 rounded-2xl px-4 py-3 text-sm font-bold text-zinc-900 focus:ring-2 focus:ring-orange-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2">
              Notes
            </label>

            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={4}
              placeholder="Add details for the company..."
              className="w-full bg-zinc-50 border border-zinc-100 rounded-2xl px-4 py-3 text-sm font-bold text-zinc-900 focus:ring-2 focus:ring-orange-500 outline-none resize-none"
            />
          </div>

          {error && (
            <div className="rounded-2xl bg-red-50 border border-red-100 text-red-600 text-sm font-bold p-3">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="rounded-2xl bg-green-50 border border-green-100 text-green-700 text-sm font-bold p-3">
              {successMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-zinc-900 text-white rounded-2xl py-4 font-black text-sm uppercase tracking-widest hover:bg-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
            {submitting ? "Sending..." : "Send Booking Request"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ServiceBookingModal;